import {
  ChartColumnIcon,
  ChannelIcon,
  Flex,
  Skeleton,
  Text,
  Tooltip,
} from '@buffer-mono/popcorn'
import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { isChannelIconService } from '~publish/pages/Community/util/isChannelIconService'

import {
  UserRepliesCount_Channel,
  useUserRepliesCount,
} from './hooks/useUserRepliesCount'
import {
  getDisplayServiceName,
  isGoogleBusinessProfile,
} from '../../../util/channelHelpers'

import styles from './CommentsStatsBanner.module.css'

import type { JSX } from 'react'

export const CommentStatsBanner_Channel = graphql(
  `
    fragment CommentStatsBanner_Channel on Channel @_unmask {
      service
      ...UserRepliesCount_Channel
    }
  `,
  [UserRepliesCount_Channel],
)

/**
 * `isGoogleBusinessProfile` still types its argument against the full codegen
 * `Channel`, so channels flowing through this banner must satisfy both it and
 * the colocated fragment. The intersection collapses to the fragment type once
 * that helper migrates (CORE-6384).
 */
export type CommentStatsBannerChannel = FragmentOf<
  typeof CommentStatsBanner_Channel
> &
  NonNullable<Parameters<typeof isGoogleBusinessProfile>[0]>[number]

type CommentStatsBannerProps = {
  channels: CommentStatsBannerChannel[]
  postId?: string
}

export const CommentStatsBanner = ({
  channels,
  postId,
}: CommentStatsBannerProps): JSX.Element | null => {
  const { repliesCount, loading, error, effectiveLimit } = useUserRepliesCount({
    channels,
    postId,
    days: 7,
  })

  if (loading) {
    return <Skeleton height={50} />
  }

  if (error || channels.length === 0) {
    return null
  }

  const isGBP = isGoogleBusinessProfile(channels)
  const isSingleChannel = channels.length === 1

  const limitReached =
    effectiveLimit !== null &&
    repliesCount !== null &&
    repliesCount >= effectiveLimit

  const displayCount = limitReached ? `+${effectiveLimit}` : repliesCount

  return (
    <Flex align="center" justify="between" className={styles.container}>
      <Text
        size="sm"
        color="default"
        icon={<ChartColumnIcon size="small" color="subtle" />}
      >
        You&apos;ve replied to {displayCount} {isGBP ? 'review' : 'comment'}
        {repliesCount !== 1 ? 's' : ''} within the last 7 days
      </Text>
      <Text size="sm" color="subtle" className={styles.nativeReplies}>
        {isSingleChannel ? (
          <>
            Includes replies via{' '}
            {isChannelIconService(channels[0].service) && (
              <ChannelIcon
                type={channels[0].service}
                size="xsmall"
                color="subtle"
              />
            )}{' '}
            {getDisplayServiceName(channels[0].service)}
          </>
        ) : (
          <Tooltip content="We show replies you've sent both via Buffer and natively using social media platforms.">
            <span className={styles.nativeRepliesLabel}>
              Includes native replies
            </span>
          </Tooltip>
        )}
      </Text>
    </Flex>
  )
}
